import { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeAppSyncEvents,
  publishAppSyncEvent,
  appsyncEventEndpoints, streamEventEndpoints,
  type AppSyncEventsUnsubscribe,
} from '../services/appsyncEvents';
import { getRealtimeConfig } from '../services/dataPlaneApi';

export type AgentChannelMessage = Record<string, unknown>;

/**
 * Soft cap on the in-memory envelope buffer per room. Long-running rooms
 * (multi-hour executions, dozens of approvals) can stream tens of thousands
 * of events; rendering history is paged from `/networks/{id}/channel-events`,
 * so the live buffer only needs to hold the recent window. We trim the
 * oldest entries when we exceed this size — `correlationId` survives because
 * it lives on each envelope, not on the buffer.
 */
export const AGENT_CHANNEL_SOFT_CAP = 500;

/**
 * LIFECYCLE-PRESERVING BUFFER (REALTIME-HUB.md §0.4 — the 2026-09-25 bouncy-ape incident). The room
 * RECOMPUTES its whole state (roster, run blocks, statuses) from this buffer on every change. The old
 * "keep the newest 500 of ANY type" trim let 5 streaming builders + f.synth push the few LIFECYCLE
 * envelopes out (USER_PROMPT / EXECUTION_STARTED / BUILDER_PHASE COMPLETED / AGENT_FINAL_RESPONSE):
 * finished agents re-derived as ``running`` from their surviving LOG_LINEs, the run block regrouped
 * (the panel vanished and came back), and a hard refresh "fixed" it by reloading persisted lifecycle.
 * Truncating an event-sourced log truncates state. Now ONLY high-volume STREAM envelopes are
 * evictable (cap ``AGENT_CHANNEL_STREAM_CAP``); lifecycle envelopes are kept (tens per run), with a
 * very high safety ceiling for multi-hour rooms.
 */
export const AGENT_CHANNEL_STREAM_CAP = 1500;
export const AGENT_CHANNEL_LIFECYCLE_CAP = 5000;
export const STREAM_EVENT_TYPES = new Set([
  'LOG_LINE', 'LOG_BATCH', 'SUMMARY_DELTA', 'AGENT_REASONING', 'SFN_TRANSITION',
  'STATUS_UPDATE', 'ROOM_STATE_CHANGED',
  'CONTEXT_AGENT_DELTA',   // an Ask's streamed answer (cumulative — only the newest matters; MODES.md §10.2a)
]);

function envelopeTypeOf(e: AgentChannelMessage): string {
  const ev = (e || {}) as Record<string, unknown>;
  return String(ev.eventType ?? ev.type ?? '');
}

/** Which agent a stream envelope belongs to (its live terminal) — '' for run-level streams. */
function streamOwnerOf(e: AgentChannelMessage): string {
  const ev = (e || {}) as Record<string, unknown>;
  const p = (ev.payload || {}) as Record<string, unknown>;
  return String(p.agentId ?? ev.agentId ?? '');
}

/** Minimum stream envelopes every agent keeps, however loud the others are. */
export const AGENT_CHANNEL_STREAM_MIN_PER_AGENT = 150;

/** Trim the live buffer WITHOUT ever evicting lifecycle envelopes. Stream envelopes are trimmed with a
 *  PER-AGENT FAIR SHARE (REALTIME-HUB.md §0.4): a loud agent (f.synth's 2,017 deltas) evicts only ITS
 *  OWN oldest lines — a quiet builder's live terminal can never be emptied by someone else's flood.
 *  Lifecycle yields only past a very high safety ceiling. Order preserved. Pure (unit-tested). */
export function trimChannelBuffer(
  buf: AgentChannelMessage[],
  streamCap: number = AGENT_CHANNEL_STREAM_CAP,
  lifecycleCap: number = AGENT_CHANNEL_LIFECYCLE_CAP,
): AgentChannelMessage[] {
  const perOwner = new Map<string, number>();
  let stream = 0;
  let life = 0;
  for (const e of buf) {
    if (STREAM_EVENT_TYPES.has(envelopeTypeOf(e))) {
      stream += 1;
      const o = streamOwnerOf(e);
      perOwner.set(o, (perOwner.get(o) || 0) + 1);
    } else life += 1;
  }
  if (stream <= streamCap && life <= lifecycleCap) return buf;
  // Fair share: each owner keeps its NEWEST ``share`` stream envelopes (never below the per-agent floor).
  const owners = Math.max(1, perOwner.size);
  const share = Math.max(AGENT_CHANNEL_STREAM_MIN_PER_AGENT, Math.floor(streamCap / owners));
  const dropFor = new Map<string, number>();
  if (stream > streamCap) {
    for (const [o, n] of perOwner) dropFor.set(o, Math.max(0, n - share));
  }
  let dropLife = Math.max(0, life - lifecycleCap);
  const out: AgentChannelMessage[] = [];
  for (const e of buf) {                                    // oldest first → drop the oldest
    if (STREAM_EVENT_TYPES.has(envelopeTypeOf(e))) {
      const o = streamOwnerOf(e);
      const d = dropFor.get(o) || 0;
      if (d > 0) { dropFor.set(o, d - 1); continue; }
    } else if (dropLife > 0) { dropLife -= 1; continue; }
    out.push(e);
  }
  return out;
}

/**
 * Stable per-envelope dedupe key. Mirrors the priority order used by
 * `services/workbenchRealtime.dedupeKey` so the two pipelines suppress the
 * same logical duplicate when both transports deliver it (legacy WS +
 * AppSync replay during reconnect catch-up). Falls back to a composite key
 * so envelopes that lost their server-stamped `eventId` still collapse on
 * exact retransmits.
 */
export function agentChannelEventKey(raw: AgentChannelMessage): string {
  if (!raw || typeof raw !== 'object') return `raw:${String(raw)}`;
  const ev = raw as Record<string, unknown>;
  if (typeof ev.eventId === 'string' && ev.eventId.length > 0) return `ev:${ev.eventId}`;
  if (typeof ev._appsyncEventId === 'string' && ev._appsyncEventId.length > 0) {
    return `as:${ev._appsyncEventId}`;
  }
  const t = String(ev.eventType ?? ev.type ?? '');
  const ts = String(ev.timestamp ?? ev.created_at ?? '');
  const corr = String(ev.correlationId ?? '');
  return `c:${t}:${corr}:${ts}`;
}

/**
 * Cognito scope segment for AppSync channel path — must match backend
 * ``status_broadcaster`` / ``stepflow_core.events.scope_segment`` and the
 * sync stack ``onSubscribe`` authorizer (tenant id or ``user-{sub}``).
 * AppSync channel segments are strictly ``[A-Za-z0-9-]`` — never use
 * underscores in the personal fallback.
 */
export function appsyncChannelScope(
  activeTenantId: string | undefined,
  cognitoSub: string | undefined,
): string | undefined {
  const t = (activeTenantId || '').trim();
  if (t) return t;
  const sub = (cognitoSub || '').trim();
  return sub ? `user-${sub}` : undefined;
}

export const DEFAULT_POOL_ID = 'default';

/**
 * RECONNECT BACKFILL merge (REALTIME-HUB.md §0.3): fold a freshly fetched history page into the
 * existing chronological history — dedupe by the SAME key the live buffer uses, keep chronological
 * order. Pure (unit-tested); ``fetchedNewestFirst`` is the channel-events page as returned.
 */
export function mergeBackfill(
  history: AgentChannelMessage[],
  fetchedNewestFirst: AgentChannelMessage[],
): AgentChannelMessage[] {
  const seen = new Set(history.map(agentChannelEventKey));
  const fresh = [...(fetchedNewestFirst || [])].reverse().filter((e) => {
    const k = agentChannelEventKey(e);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (!fresh.length) return history;
  return [...history, ...fresh].sort(compareEnvelopeTimestamps);
}

function normalizePoolId(poolId: string | undefined): string {
  const p = (poolId || '').trim();
  return p || DEFAULT_POOL_ID;
}

/**
 * Compare two envelopes for chronological order. Falls back to insertion
 * order (returns 0) when neither has a parseable timestamp so callers
 * preserve arrival order for mid-flight transitions like LOG_BATCH bursts.
 */
export function compareEnvelopeTimestamps(a: AgentChannelMessage, b: AgentChannelMessage): number {
  const ta = parseEnvelopeTs(a);
  const tb = parseEnvelopeTs(b);
  if (ta === null || tb === null) return 0;
  return ta - tb;
}

function parseEnvelopeTs(raw: AgentChannelMessage): number | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const v = o.timestamp ?? o.created_at ?? o.serverTimestamp ?? o.server_timestamp;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.length > 0) {
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseAgentEventEntry(e: unknown): AgentChannelMessage {
  if (typeof e === 'string') {
    try {
      return JSON.parse(e) as AgentChannelMessage;
    } catch {
      return { raw: e } as AgentChannelMessage;
    }
  }
  if (e && typeof e === 'object' && !Array.isArray(e)) {
    return e as AgentChannelMessage;
  }
  return { raw: e } as AgentChannelMessage;
}

/**
 * AppSync Events — subscribe on ``/default/agent/{scope}/{networkId}/{poolId}``.
 *
 * ``poolId`` is the runtime network-tenant (customer pool); pass ``"default"``
 * for creator/build views where no runtime tenant has been selected yet.
 * Must stay in sync with the sync-stack ``onSubscribe`` authorizer and the
 * backend ``stepflow_core.events`` scope derivation.
 *
 * Transport is the shared ``services/appsyncEvents`` singleton, so mounting
 * this hook alongside ``useUserChannel`` / ``useWorkbenchChannel`` adds a
 * multiplexed subscription on the existing WebSocket rather than opening
 * a brand new one.
 *
 * Env (from SSM / CI): ``VITE_APPSYNC_EVENTS_HTTP_DNS``,
 * ``VITE_APPSYNC_EVENTS_REALTIME_DNS``.
 */
export function useAgentChannel(
  networkId: string | undefined,
  channelScope: string | undefined,
  poolId?: string,
) {
  const effectivePoolId = normalizePoolId(poolId);
  const [messages, setMessages] = useState<AgentChannelMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // RECONNECT EPOCH (REALTIME-HUB.md §0.3): bumps each time the socket comes back AFTER having been
  // live. AppSync Events has no replay, so anything published during the gap is missing from
  // ``messages`` — consumers watch this to BACKFILL (history page + builder log tails).
  const [reconnectEpoch, setReconnectEpoch] = useState(0);
  const wasLiveRef = useRef(false);
  const droppedRef = useRef(false);

  // Dedupe ledger lives in a ref so the soft cap doesn't repeatedly trip
  // React renders. Cleared in lockstep with `messages` on pool/scope pivot.
  const seenKeysRef = useRef<Set<string>>(new Set());

  const { httpDns, realtimeDns } = appsyncEventEndpoints();
  // REALTIME LANES (REALTIME-HUB.md §0.6): the STREAM Event API's endpoints ('' = one-API mode)
  const [streamEp, setStreamEp] = useState<{ httpDns: string; realtimeDns: string }>({ httpDns: '', realtimeDns: '' });
  useEffect(() => {
    let alive = true;
    streamEventEndpoints(getRealtimeConfig).then((ep) => { if (alive) setStreamEp(ep); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    seenKeysRef.current = new Set();
    setMessages([]);
    wasLiveRef.current = false;
    droppedRef.current = false;
  }, [networkId, channelScope, effectivePoolId]);

  useEffect(() => {
    if (!networkId || !channelScope || !httpDns || !realtimeDns) {
      setConnected(false);
      return undefined;
    }

    setError(null);
    const channelPath = `/default/agent/${channelScope}/${networkId}/${effectivePoolId}`;

    let active = true;
    // ONE merge path for BOTH sockets (state + stream): the same dedupe ledger + lifecycle-preserving trim
    const onData = (events: unknown[]) => {
          if (!active) return;
          const fresh: AgentChannelMessage[] = [];
          for (const e of events) {
            const parsed = parseAgentEventEntry(e);
            const key = agentChannelEventKey(parsed);
            if (seenKeysRef.current.has(key)) continue;
            seenKeysRef.current.add(key);
            fresh.push(parsed);
          }
          if (!fresh.length) return;
          // Most arrivals are already chronological; sort defensively so a
          // late-delivered envelope doesn't break the reducer's monotonic
          // cursor (`AgenticNetworkRoom.msgCursorRef`).
          fresh.sort(compareEnvelopeTimestamps);
          // Lifecycle-preserving trim (see trimChannelBuffer) — never a blind ``slice(-N)``.
          setMessages((prev) => trimChannelBuffer([...prev, ...fresh]));
    };
    const unsubscribe: AppSyncEventsUnsubscribe = subscribeAppSyncEvents(
      httpDns,
      realtimeDns,
      channelPath,
      {
        onData,
        onStatus: (status) => {
          if (!active) return;
          setConnected(status === 'connected');
          if (status === 'connected') {
            if (wasLiveRef.current && droppedRef.current) setReconnectEpoch((e) => e + 1);
            wasLiveRef.current = true;
            droppedRef.current = false;
          } else if (status === 'disconnected' && wasLiveRef.current) {
            droppedRef.current = true;
          }
        },
        onError: (err, kind) => {
          if (!active) return;
          setError(
            typeof err === 'string'
              ? err
              : JSON.stringify(err ?? { kind }),
          );
        },
      },
    );

    // THE STREAM SOCKET (same channel, its own API): agent text + reasoning. Its drop also bumps the reconnect epoch
    // so the builder log tails BACKFILL what the gap missed (AppSync Events has no replay).
    let streamWasLive = false;
    let streamDropped = false;
    const unsubscribeStream: AppSyncEventsUnsubscribe | null = (streamEp.httpDns && streamEp.realtimeDns)
      ? subscribeAppSyncEvents(streamEp.httpDns, streamEp.realtimeDns, channelPath, {
        onData,
        onStatus: (status) => {
          if (!active) return;
          if (status === 'connected') {
            if (streamWasLive && streamDropped) setReconnectEpoch((e) => e + 1);
            streamWasLive = true;
            streamDropped = false;
          } else if (status === 'disconnected' && streamWasLive) {
            streamDropped = true;
          }
        },
        onError: () => { /* the state socket owns the room's error banner; the stream heals itself */ },
      })
      : null;

    return () => {
      active = false;
      unsubscribe();
      if (unsubscribeStream) unsubscribeStream();
    };
  }, [networkId, channelScope, effectivePoolId, httpDns, realtimeDns, streamEp.httpDns, streamEp.realtimeDns]);

  const sendMessage = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!networkId || !channelScope || !httpDns) {
        throw new Error('Missing channel or VITE_APPSYNC_EVENTS_HTTP_DNS');
      }
      const channel = `default/agent/${channelScope}/${networkId}/${effectivePoolId}`;
      await publishAppSyncEvent(httpDns, channel, payload);
    },
    [networkId, channelScope, effectivePoolId, httpDns],
  );

  return { messages, sendMessage, connected, error, reconnectEpoch };
}
